// Protects the /, /devices and /dev/mfa routes: calls /api/auth/me on mount.
// 401 (or null) → send the user straight to the identity provider (id.{root}
// B2C signin) and back here on success. Identity lives at `id`, not at a
// landing page — there is no `start` intermediary. The session payload is
// cached via TanStack Query so subsequent renders don't re-fetch.

import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { authClient } from "./auth";
import { resolveBrand, surfaceUrl } from "./brand";
import { asSessionView, type SessionView } from "./session";

interface RequireAuthProps {
  children: (session: SessionView) => ReactNode;
}

export function RequireAuth({ children }: RequireAuthProps) {
  const me = useQuery({
    queryKey: ["auth", "me"],
    queryFn: () => authClient.me(),
    retry: false,
    staleTime: 60_000,
  });

  if (me.isLoading) {
    return (
      <main className="hub-page-header">
        <p>Checking your session…</p>
      </main>
    );
  }

  if (me.isError || !me.data) {
    // Unauthenticated on a protected surface → straight to the IdP. The adapter
    // 302s to B2C SIGNIN on id.{root} and returns here on success. Done as a
    // render side-effect to keep the surface deterministic on first load.
    // loginRedirect navigates then throws by contract (it "never returns"), so
    // swallow the post-navigation throw.
    const brand = resolveBrand();
    if (typeof window !== "undefined") {
      try {
        authClient.loginRedirect({
          brand,
          flow: "signin",
          return: surfaceUrl("online", brand) + "/",
        });
      } catch {
        // navigation already initiated; the throw is the never-return guard
      }
    }
    return null;
  }

  return <>{children(asSessionView(me.data))}</>;
}
